/**
 * Тонкая обёртка над window.Telegram.WebApp с типами.
 *
 * Зачем не использовать SDK напрямую: SDK работает, но добавляет
 * сложность с реактивностью. Для наших нужд достаточно: получить
 * initData (для аутентификации), themeParams (для подстройки темы),
 * пользовательские данные, и пара методов вроде hapticFeedback.
 *
 * В DEV-режиме (когда открыто в обычном браузере) — возвращаем мок,
 * чтобы можно было разрабатывать без Telegram.
 */

export interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  photo_url?: string;
}

export interface TelegramThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  header_bg_color?: string;
  accent_text_color?: string;
  section_bg_color?: string;
  section_header_text_color?: string;
  subtitle_text_color?: string;
  destructive_text_color?: string;
}

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: TelegramUser;
    auth_date?: number;
    hash?: string;
    start_param?: string;
  };
  colorScheme: 'light' | 'dark';
  themeParams: TelegramThemeParams;
  viewportHeight: number;
  viewportStableHeight: number;
  isExpanded: boolean;
  platform: string;
  version: string;

  ready: () => void;
  expand: () => void;
  close: () => void;
  enableClosingConfirmation: () => void;
  disableClosingConfirmation: () => void;

  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };

  MainButton: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    isActive: boolean;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    setText: (text: string) => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };

  BackButton: {
    isVisible: boolean;
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };

  onEvent: (eventType: string, cb: () => void) => void;
  offEvent: (eventType: string, cb: () => void) => void;

  showAlert: (message: string, cb?: () => void) => void;
  showConfirm: (message: string, cb?: (ok: boolean) => void) => void;
  showPopup: (
    params: { title?: string; message: string; buttons?: Array<{ id?: string; type?: 'default' | 'ok' | 'close' | 'cancel' | 'destructive'; text?: string }> },
    cb?: (id: string) => void,
  ) => void;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp;
    };
  }
}

/**
 * Мок для DEV-режима (открытие в обычном браузере без Telegram).
 * Позволяет разрабатывать UI, не запуская Mini App каждый раз.
 */
const createMockWebApp = (): TelegramWebApp => {
  const noop = () => {};
  return {
    initData: 'mock_init_data',
    initDataUnsafe: {
      user: {
        id: 123456789,
        first_name: 'Dev',
        last_name: 'User',
        username: 'devuser',
        language_code: 'ru',
      },
      auth_date: Math.floor(Date.now() / 1000),
    },
    colorScheme: 'dark',
    themeParams: {},
    viewportHeight: window.innerHeight,
    viewportStableHeight: window.innerHeight,
    isExpanded: true,
    platform: 'web',
    version: '7.0',
    ready: noop,
    expand: noop,
    close: noop,
    enableClosingConfirmation: noop,
    disableClosingConfirmation: noop,
    HapticFeedback: {
      impactOccurred: noop,
      notificationOccurred: noop,
      selectionChanged: noop,
    },
    MainButton: {
      text: '',
      color: '',
      textColor: '',
      isVisible: false,
      isActive: false,
      show: noop,
      hide: noop,
      enable: noop,
      disable: noop,
      setText: noop,
      onClick: noop,
      offClick: noop,
    },
    BackButton: {
      isVisible: false,
      show: noop,
      hide: noop,
      onClick: noop,
      offClick: noop,
    },
    onEvent: noop,
    offEvent: noop,
    showAlert: (msg) => alert(msg),
    showConfirm: (msg, cb) => cb?.(confirm(msg)),
    showPopup: (params, cb) => {
      alert(params.message);
      cb?.('');
    },
  };
};

/**
 * Возвращает Telegram WebApp instance.
 * В Telegram-окружении — реальный, в браузере — мок.
 */
export const getTelegramWebApp = (): TelegramWebApp => {
  if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
    return window.Telegram.WebApp;
  }
  // DEV-режим: мок
  if (import.meta.env.DEV) {
    console.info('[Telegram] Running in DEV mode with mock WebApp');
    return createMockWebApp();
  }
  throw new Error('Telegram WebApp is not available');
};

/**
 * Инициализация: вызывается один раз при старте приложения.
 * - Сообщает Telegram, что Mini App готов (убирает spinner)
 * - Расширяет на полный экран
 * - Включает подтверждение закрытия (защита от случайных свайпов)
 * - Применяет тему Telegram к CSS-переменным (если хотим следовать
 *   нативной теме). Пока — не следуем, у нас своя collector's-эстетика.
 *   Но safe-areas и viewport-height синхронизируем.
 */
export const initTelegram = () => {
  const tg = getTelegramWebApp();

  tg.ready();
  tg.expand();

  // Синхронизируем viewport-height в CSS-переменную.
  // В Telegram WebView 100vh может включать UI Telegram, поэтому
  // используем точное значение от API.
  const syncViewport = () => {
    document.documentElement.style.setProperty(
      '--tg-viewport-height',
      `${tg.viewportStableHeight}px`,
    );
  };
  syncViewport();
  tg.onEvent('viewportChanged', syncViewport);

  return tg;
};

// Утилитарные шорткаты для частых операций
export const haptic = {
  light: () => getTelegramWebApp().HapticFeedback.impactOccurred('light'),
  medium: () => getTelegramWebApp().HapticFeedback.impactOccurred('medium'),
  heavy: () => getTelegramWebApp().HapticFeedback.impactOccurred('heavy'),
  success: () => getTelegramWebApp().HapticFeedback.notificationOccurred('success'),
  warning: () => getTelegramWebApp().HapticFeedback.notificationOccurred('warning'),
  error: () => getTelegramWebApp().HapticFeedback.notificationOccurred('error'),
  selection: () => getTelegramWebApp().HapticFeedback.selectionChanged(),
};